import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { loginUser } from '../store/slices/authSlice';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useTranslation } from '../i18n';
import LegalLinks from '../components/LegalLinks';
import { translateAuthError } from '../i18n/authErrors';
import { Logo } from '../components/brand';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isLoading, error, isAuthenticated } = useAppSelector(
    state => state.auth
  );
  const [showPassword, setShowPassword] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
  });

  const fromPath = location.state?.from?.pathname as string | undefined;
  const from = !fromPath || fromPath === '/login' ? '/dashboard' : fromPath;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(loginUser(formData));
    if (loginUser.fulfilled.match(result)) {
      navigate(from, { replace: true });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-4 flex items-center justify-center">
            <Logo variant="wordmark" height={72} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('auth.login.title')}
          </h1>
          <p className="text-white/60">{t('auth.login.subtitle')}</p>
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="hc-card p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-white/70 mb-2"
              >
                {t('auth.field.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-white/35" />
                </div>
                <input
                  id="email"
                  type="email"
                  dir="ltr"
                  required
                  value={formData.email}
                  onChange={e => handleChange('email', e.target.value)}
                  className="block w-full px-10 py-3 border border-white/15 rounded-pill focus:outline-none focus:ring-2 focus:ring-hc-accent focus:border-hc-accent/60 transition-colors"
                  placeholder={t('auth.placeholder.email')}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-white/70 mb-2"
              >
                {t('auth.field.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-white/35" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  className="block w-full ps-10 pe-12 py-3 border border-white/15 rounded-pill focus:outline-none focus:ring-2 focus:ring-hc-accent focus:border-hc-accent/60 transition-colors"
                  placeholder={t('auth.placeholder.password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? t('auth.password.hide')
                      : t('auth.password.show')
                  }
                  className="absolute inset-y-0 end-0 pe-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-white/35 hover:text-white/60" />
                  ) : (
                    <Eye className="h-5 w-5 text-white/35 hover:text-white/60" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                role="alert"
                className="bg-red-500/10 border border-red-500/20 rounded-tile p-3"
              >
                <p className="text-sm text-red-400">
                  {translateAuthError(t, error)}
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-hc-accent hover:bg-hc-accent-hover text-hc-cream text-cta font-display font-extrabold py-3 px-4 rounded-pill focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hc-accent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-glow"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white me-2"></div>
                  {t('auth.login.submit.loading')}
                </div>
              ) : (
                t('auth.login.submit')
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/15"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-hc-card text-white/60">
                  {t('auth.login.noAccount')}
                </span>
              </div>
            </div>

            {/* Register Link */}
            <Link
              to="/auth/register"
              className="w-full bg-white/[0.06] text-white/70 font-medium py-3 px-4 rounded-pill hover:bg-white/[0.13] focus:outline-none focus:ring-2 focus:ring-white/25 transition-all duration-200 text-center block"
            >
              {t('auth.login.createAccount')}
            </Link>
          </form>
        </motion.div>

        {/* Footer */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-center">
          <Link
            to="/auth/forgot-password"
            className="inline-flex min-h-[44px] items-center text-sm text-hc-accent-soft hover:text-white transition-colors duration-200"
          >
            {t('auth.login.forgot')}
          </Link>
          <Link
            to="/start"
            className="inline-flex min-h-[44px] items-center text-sm font-medium text-hc-accent-soft hover:text-white transition-colors duration-200"
          >
            {t('guest.cta.play')}
          </Link>
          <Link
            to="/help"
            className="inline-flex min-h-[44px] items-center text-sm text-white/70 hover:text-white transition-colors duration-200"
          >
            {t('nav.help')}
          </Link>
        </div>
        <LegalLinks className="mt-1" />
      </motion.div>
    </main>
  );
};

export default LoginPage;
